import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { ModelError_Item } from '@treadinc/horizon-api-spec';
import axios from 'axios';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CSVImportExportButton } from '~components/CSVImportExport/CSVImportExportButton';
import { CSVImportExportModal } from '~components/CSVImportExport/CSVImportExportModal';
import { useCSVModal } from '~components/CSVImportExport/useCSVModal';
import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { createMaterialTypesConfig } from '~hooks/useCSVImportExport/configs/materialTypesConfig';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import { MaterialType } from '~hooks/useMaterials/models';
import useMaterials from '~hooks/useMaterials/useMaterials';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import connection from '~services/connectionModule';
import { Paginated } from '~services/pagination';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import MaterialTypeForm, { MaterialTypeFormHandler } from './MaterialTypeForm';
import { MaterialTypeDto, MaterialTypeDtoAsyncError } from './materialTypeSchema';

const NO_COMPANY_PAGE: Paginated<MaterialType> = {
  data: [],
  pagination: { limit: MAX_API_PAGE_SIZE, page: 1 },
};

const MaterialTypeManagement = observer(() => {
  const { userStore, toasterStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const {
    createMaterialTypeByCompanyId,
    deleteMaterialType,
    getMaterialTypesByCompanyId,
    updateMaterialType,
  } = useMaterials();
  const [isDeletingMaterialType, setIsDeletingMaterialType] = useState(false);

  const {
    goToPage: goToMaterialTypesPage,
    isFetching: isFetchingMaterialTypes,
    pagination,
    reloadCurrentPage: reloadMaterialTypesPage,
    rows,
    startOver: startMaterialTypesOver,
  } = useCursorPagination<MaterialType>({
    fetchPage: ({ cursor, limit, link, signal }) => {
      if (!companyId) {
        return Promise.resolve(NO_COMPANY_PAGE);
      }

      return getMaterialTypesByCompanyId({ companyId, cursor, limit, link, signal });
    },
    limit: MAX_API_PAGE_SIZE,
  });

  useEffect(() => {
    void startMaterialTypesOver();
  }, [companyId, startMaterialTypesOver]);

  // Get user permissions to determine if user can create/edit/delete material types
  const userPermissions = userStore.getPermissions();

  // Modal Actions
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [formDialogAsyncErrors, setFormDialogAsyncErrors] = useState<
    MaterialTypeDtoAsyncError[]
  >([]);
  const [formDialogState, setFormDialogState] = useState<{
    materialType?: MaterialType;
    isOpen: boolean;
    isSaving: boolean;
  }>({ isOpen: false, isSaving: false });

  const formDialogRef = useRef<MaterialTypeFormHandler>(null);

  const csvModal = useCSVModal({
    onImportComplete: () => void goToMaterialTypesPage(),
  });
  const csvConfig = useMemo(() => createMaterialTypesConfig(companyId), [companyId]);

  const handleEditRow = useCallback(
    (materialTypeId: string) => {
      const materialType = rows.find(({ id }) => {
        return id === materialTypeId;
      });

      if (!materialType) {
        return;
      }

      setFormDialogState({ isSaving: false, isOpen: true, materialType });
    },
    [rows],
  );

  const deleteRow = (id: string) => {
    setSelectedId(id);
    setIsDeleteDialogOpen(true);
  };

  const deleteMaterialTypeCallback = async () => {
    if (!selectedId) {
      return;
    }

    setIsDeletingMaterialType(true);
    try {
      await deleteMaterialType({ companyId, materialTypeId: selectedId });
      setIsDeleteDialogOpen(false);
      setSelectedId(null);
      void reloadMaterialTypesPage();
      toasterStore.push(
        alert(t('administration.material_type.deleted_message'), AlertTypes.success),
      );
    } catch {
      // Error is already handled in the hook
    } finally {
      setIsDeletingMaterialType(false);
    }
  };

  const closeDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setSelectedId(null);
  };

  const handleCloseFormDialog = useCallback(() => {
    setFormDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleSubmitFormDialog = useCallback(async () => {
    const formData = await formDialogRef.current?.onSubmit();

    if (!formData) {
      return;
    }

    const materialTypeId = formDialogState.materialType?.id;

    try {
      setFormDialogAsyncErrors([]);
      setFormDialogState((state) => ({ ...state, isSaving: true }));

      const materialTypeData = new MaterialType(
        formData.name,
        materialTypeId ?? '',
        false,
        formData.externalId || null,
        '',
      );

      if (materialTypeId) {
        const updated = await updateMaterialType({
          companyId,
          materialType: materialTypeData,
        });

        void reloadMaterialTypesPage();
        toasterStore.push(
          alert(
            t('administration.material_type.updated_message', { name: updated.name }),
            AlertTypes.success,
          ),
        );
      } else {
        const created = await createMaterialTypeByCompanyId({
          companyId: formData.company.value,
          materialType: materialTypeData,
        });

        // refetch the displayed company's list so the grid reflects reality regardless of which
        // company the type was created for (the form allows picking any available company)
        void goToMaterialTypesPage();

        toasterStore.push(
          alert(
            t('administration.material_type.created_message', { name: created.name }),
            AlertTypes.success,
          ),
        );
      }

      setFormDialogState({ isSaving: false, isOpen: false });
    } catch (error) {
      setFormDialogState((state) => ({ ...state, isSaving: false }));

      // Non-422 errors are already toasted by the hook. 422 errors are re-thrown
      // by the hook so the dialog can surface field-level validation messages.
      if (axios.isAxiosError(error) && error.response?.status === 422) {
        const errorsArray = (error.response?.data?.error?.errors ??
          []) as ModelError_Item[];

        const asyncErrors: MaterialTypeDtoAsyncError[] = [];
        const formFieldByApiField: Record<string, keyof MaterialTypeDto> = {
          name: 'name',
          external_id: 'externalId',
        };

        errorsArray.forEach((errorsArrayItem) => {
          const { field, message } = errorsArrayItem;
          const formField = formFieldByApiField[field];

          if (formField) {
            asyncErrors.push({ field: formField, message });
          }
        });

        if (asyncErrors.length) {
          setFormDialogAsyncErrors(asyncErrors);
        } else {
          try {
            connection.handleRequestError(
              error,
              materialTypeId
                ? t('error_messages.material_types.failed_to_update')
                : t('error_messages.material_types.failed_to_create'),
            );
          } catch {
            // handleRequestError re-throws after showing the toast; swallow it here so the
            // submit handler doesn't surface an unhandled promise rejection
          }
        }
      }
    }
  }, [
    companyId,
    formDialogState.materialType?.id,
    goToMaterialTypesPage,
    reloadMaterialTypesPage,
  ]);

  useEffect(() => {
    if (!formDialogState.isOpen) {
      setFormDialogAsyncErrors([]);
    }
  }, [formDialogState.isOpen]);

  const columns = useMemo<Array<DataTableColumn<MaterialType>>>(
    () => [
      {
        field: 'name',
        headerName: t('form_fields.name'),
        valueGetter: (materialType) => materialType.name,
      },
      {
        field: 'externalId',
        headerName: t('form_fields.external_id'),
        valueGetter: (materialType) => materialType.externalId,
      },
      {
        field: 'source',
        headerName: t('form_fields.source'),
        valueGetter: (materialType) => materialType.source,
      },
      actionsColumn((materialType) => {
        const options =
          userPermissions.canDeleteMaterialType && !materialType.system
            ? [
                {
                  title: t('actions.delete'),
                  icon: <Delete />,
                  callBack: () => deleteRow(materialType.id),
                },
              ]
            : [];
        return (
          <>
            {userPermissions.canEditMaterialType && !materialType.system && (
              <IconButton size="small" onClick={() => handleEditRow(materialType.id)}>
                <Edit />
              </IconButton>
            )}

            {options && (
              <SimpleMenu options={options}>
                <MoreHoriz />
              </SimpleMenu>
            )}
          </>
        );
      }),
    ],
    [rows],
  );

  return (
    <>
      <SettingsPageHeader
        title={t('administration.material_type.title')}
        description={t('settings.page_descriptions.material_types')}
      />
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mb: 2 }}>
        <CSVImportExportButton onClick={csvModal.open} />
        <SmallButton
          color="brandV2Yellow"
          disabled={!userPermissions.canCreateMaterialType}
          onClick={() => setFormDialogState({ isOpen: true, isSaving: false })}
          startIcon={<Add />}
        >
          {t('administration.material_type.create_title')}
        </SmallButton>
      </Box>
      <Box>
        <DataTable
          id="material-types-data-grid"
          columns={columns}
          rows={rows}
          loading={isFetchingMaterialTypes || isDeletingMaterialType}
          headerTrailingComponent={
            <DataGridPager
              isFetching={isFetchingMaterialTypes || isDeletingMaterialType}
              onNavigate={(link) => void goToMaterialTypesPage(link)}
              pagination={pagination}
            />
          }
        />
      </Box>
      <Dialog
        isOpen={formDialogState.isOpen}
        preventCloseOnBackdropClick
        preventCloseOnEscapeKeyDown
      >
        <DialogHeader onClose={handleCloseFormDialog}>
          {formDialogState.materialType
            ? t('administration.material_type.update_title')
            : t('administration.material_type.create_title')}
        </DialogHeader>

        <DialogContent>
          <MaterialTypeForm
            // remount on identity change so create never inherits a prior edit's field values
            key={formDialogState.materialType?.id ?? 'create'}
            ref={formDialogRef}
            asyncErrors={formDialogAsyncErrors}
            materialType={formDialogState.materialType}
          />
        </DialogContent>

        <DialogActions>
          {Boolean(formDialogAsyncErrors.length) && (
            <ShakyErrorIndicator
              message={t('administration.users.user_form.invalid_data_error_messages')}
            />
          )}

          <SmallButton
            color="secondary"
            disabled={formDialogState.isSaving}
            onClick={handleCloseFormDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            loading={formDialogState.isSaving}
            onClick={handleSubmitFormDialog}
          >
            {formDialogState.materialType ? t('actions.update') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        isBusy={isDeletingMaterialType}
        isOpen={isDeleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={deleteMaterialTypeCallback}
        title={t('administration.material_type.delete_title')}
      >
        <Typography>{t('administration.material_type.delete_text')}</Typography>
      </ConfirmationDialog>
      <CSVImportExportModal
        isOpen={csvModal.isOpen}
        onClose={csvModal.close}
        config={csvConfig}
        companyId={companyId}
        entityDisplayName={t('administration.material_type.title')}
        onImportComplete={csvModal.handleImportComplete}
      />
    </>
  );
});

export { MaterialTypeManagement };
